import { IClientAPI, ClientAPIWrapper, TransferOptions, PlayPresentationOptions } from '@tencent/td-open-client-api';
import i18n from '@i18next/i18n';
import ExportImageManager from '@src/slide/application/service/export-image-manager';
import { IPlayManager } from '@src/slide/application/service/play-service/play-manager.interface';
import { ISelectionService } from '@src/slide/application/service/selection/selection-service.interface';
import { IEditAreaCanvasSizeManager } from '@src/slide/application/service/edit-area-canvas-size-manager/edit-area-canvas-size-manager.interface';
import { SlideDataServices } from '@src/slide/domain/slide-app/core/slide-data-services.core';
import { canvasToPt } from '@src/slide/infrastructure/module/independent/unit';
import { browser } from '@src/slide/infrastructure/module/independent/agent/common';
import playerStore from '@player/src/store';
import { nextPage, prevPage } from '@player/src/actions/control';
import eventBus, { EventKeys } from '@src/slide/infrastructure/module/independent/event-emitter/instance';
import { isPreview } from '@src/slide/infrastructure/module/dependent/runtime-environment/drive-env';

interface IPageInfo {
    pageIndex: number;
}

enum ViewState {
    VIEW = 'View', // 预览态
    PLAYING = 'Playing' // 演示态
}

// 视图缩放
const MIN_ZOOM = 0.1;
const MAX_ZOOM = 4;

const LOG_TAG = '[OpenClientApi]';

/**
 * 对外暴露的 open api 接口实现
 * pc & mobile 通用
 */
class OpenClientApiManager implements IClientAPI {
    public constructor(
        protected playManager: IPlayManager,
        protected selectionService: ISelectionService,
        protected editAreaCanvasSizeManager: IEditAreaCanvasSizeManager,
        private exportImageManager: ExportImageManager,
    ) {
        this.connect();
    }

    /**
     * 导出为 pdf
     * @param options
     */
    public transferToPDF(options?: TransferOptions): void {
        import('@tencent/docs-scenario-components-export-pdf').then(({ ExportPDFManager }) => {
            ExportPDFManager.getInstance().onlineExport(options);
        }).catch((err) => {
            window.log.error(`${LOG_TAG} @tencent/docs-scenario-components-export-pdf load error - ${err}`);
            const message: string = i18n.t('请求失败，请稍后重试。');
            SlideDataServices.UI.Snackbar.promise().then((snackbar) => {
                snackbar.show({
                    message,
                    autoClose: true,
                    duration: 5000,
                });
            });
            options?.onFinish?.(new Error(message), { url: '' });
        });
    }

    /**
     * 导出为图片
     * @param options
     */
    public transferToImage(options?: TransferOptions): void {
        const { onCancel, ...otherOptions } = options ?? {};
        const finalOptions = {
            ...otherOptions,
            onClose: onCancel,
            showSafeAreaInset: false,
        };
        this.exportImageManager.exportImage(finalOptions);
    }

    /**
     * 获取总页数
     */
    public getPagesNumber(): number {
        return this.selectionService?.slides?.length ?? 0;
    }

    /**
     * 获取当前页
     */
    public getCurrentPageInfo(): IPageInfo {
        return {
            pageIndex: browser.isMobile
                ? this.selectionService?.mobileCurrentDisplayIndex
                : this.selectionService?.currentSlideIndex,
        };
    }

    /**
     * 跳转到指定页
     */
    public jumpTo(pageIndex: number | string): void {
        const finalPageIndex = Number(pageIndex);
        if (isNaN(finalPageIndex)) {
            window.log.error(`${LOG_TAG} jumpTo pageIndex is NaN`);
            return;
        }
        this.selectionService.changeCurrentSlideIndexByUserAction(finalPageIndex);
    }

    /**
     * 获取播放状态
     */
    public getViewState(): string {
        return this.playManager.isPlaying ? ViewState.PLAYING : ViewState.VIEW;
    }

    /**
     * 播放幻灯片
     * @param options PlayPresentationOptions
     * @param options.isFromCurrent 从当前页播放 (仅 PC 支持该参数)
     * @param options.onFinish 退出播放态时回调
     */
    public playPresentation(options: PlayPresentationOptions): void {
        if (this.playManager.isPlaying) {
            return;
        }
        if (!SlideDataServices.ContentStatusManager()?.isDocumentReady?.()) {
            SlideDataServices.UI.Snackbar.promise().then((snackbar) => {
                snackbar.show({
                    message: i18n.t('加载幻灯片中，请稍后再试'),
                    autoClose: true,
                    duration: 3000,
                });
            });
            return;
        }
        this.playManager.playAction?.(options?.isFromCurrent ?? false);
        eventBus.once(EventKeys.END_PLAY, () => {
            options?.onFinish?.();
        });
    }

    /**
     * 退出播放
     * @param pageIndex 退出播放后定位到指定页 (仅 PC 支持该参数)
     */
    public exitPresentation(pageIndex?: number): void {
        if (!this.playManager.isPlaying) {
            return;
        }
        this.playManager.playEndAction?.(pageIndex);
    }

    /**
     * 幻灯片下一步动画
     * 非演示态不支持切换
     */
    public gotoNextAnimation(): void {
        if (!this.playManager.isPlaying) {
            window.log.error(`${LOG_TAG} gotoNextAnimation current is not playing viewState`);
            return;
        }
        playerStore.dispatch(nextPage());
    }

    /**
     * 幻灯片上一步动画
     * 非演示态不支持切换
     */
    public gotoPrevAnimation(): void {
        if (!this.playManager.isPlaying) {
            window.log.error(`${LOG_TAG} gotoNextAnimation current is not playing viewState`);
            return;
        }
        playerStore.dispatch(prevPage());
    }

    /**
     * 仅 PC
     * 视图缩放
     */
    public viewZoom(ratio: number): void {
        if (browser.isMobile) {
            return;
        }
        let message = '';
        let scale = ratio;
        if (scale <= MIN_ZOOM) {
            scale = MIN_ZOOM;
            message = i18n.t('已拉伸至最小比例');
        } else if (scale > MAX_ZOOM) {
            scale = MAX_ZOOM;
            message = i18n.t('已拉伸至最大值');
        }
        if (message) {
            SlideDataServices.UI.Snackbar.promise().then((snackbar) => {
                snackbar.show({
                    message,
                    duration: 1000,
                });
            });
        }
        window.log.error(`${LOG_TAG} viewZoom origin ratio: ${ratio}, final scale: ${scale}`);
        this.editAreaCanvasSizeManager.setCanvasScaleAndIsAutoAdapter(canvasToPt(scale), false);
        window.dispatchEvent(new CustomEvent('resize', { detail: { countSizeAgain: true } })); // 主动触发window resize
    }

    // 云编查看者打开移动端顶部更多菜单栏
    public expandMobileOptionsMenu(): void {
        eventBus.emit(EventKeys.TDOC_SHOW_MENU_MORE);
    }

    /**
     * 启动品类侧和drive侧的连接 (仅 drive 预览页)
     */
    private connect(): void {
        if (!isPreview()) {
            return;
        }
        const clientApiWrapper = new ClientAPIWrapper(this);
        // 与宿主初始化连接
        clientApiWrapper.configure(window);
    }
}

export default OpenClientApiManager;
